import { computed, effect, Injectable, signal } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { getCookie, setCookie, TOKEN_COOKIE_NAME } from '../util/cookie';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly token = signal<string | null>(null);
  private readonly widgetId = signal<string | null>(null);

  readonly currentParticipantId = computed(() => (this.token() ? this.extractParticipantId(this.token()!) : ''));

  constructor() {
    effect(() => {
      if (!this.widgetId()) {
        return;
      }
      if (this.token() === null) {
        setCookie(this.cookieName(this.widgetId()), '', 0);
        return;
      }
      setCookie(this.cookieName(this.widgetId()), this.token());
    });
  }

  loadSession(widgetId: string): void {
    this.widgetId.set(widgetId);
    const token = getCookie(this.cookieName(widgetId)) || getCookie(TOKEN_COOKIE_NAME) || null;
    if (token && this.jwtMatchesWidgetId(token, widgetId)) {
      this.token.set(token);
    } else {
      this.token.set(null);
    }
  }

  getToken(): string | null {
    return this.token();
  }

  setToken(token: string): void {
    this.token.set(token);
  }

  clearToken(namespace?: string): void {
    if (namespace) {
      setCookie(this.cookieName(namespace), '', 0);
    }
    this.token.set(null);
  }

  hasValidToken(): boolean {
    const token = this.getToken();
    return !!token && this.jwtMatchesWidgetId(token, this.widgetId());
  }

  private cookieName(widgetId: string): string {
    return `${TOKEN_COOKIE_NAME}_${widgetId}`;
  }

  // This method is used to check that the widget id matches.
  // it does not verify the token is valid, or any security related checks.
  private jwtMatchesWidgetId(token: string, widgetId: string): boolean {
    const payload = JSON.parse(window.atob(token.split('.')[1]));
    return payload?.['widget_id'] === widgetId;
  }

  private extractParticipantId(token: string | null): string {
    if (!token) {
      return '';
    }

    try {
      return jwt_decode<{ participant_id: string }>(token).participant_id;
    } catch {
      return '';
    }
  }
}
